import React, { useState, useEffect } from 'react';
import { getStrengthProgressSummary } from '../../../services/oneRmService';
import StrengthTierList from './StrengthTierList';
import { colors, typography, radius, spacing } from '../../../theme';

/**
 * Strength Progress — three-tier progressive disclosure (Top Improvements /
 * Needs Attention / New Baselines) instead of a dense exercise table.
 * "View all" opens AllExercisesDrilldown, wired by the parent ProgressTab.
 */
const StrengthProgress = ({ userId, onViewAll, showRange = false, preloadedSummary }) => {
  const [summary, setSummary] = useState(preloadedSummary ?? null);
  const [loading, setLoading] = useState(!preloadedSummary);

  useEffect(() => {
    // The coach Performance tab already computes this exact summary inside
    // loadPerformanceAnalytics and passes it down — avoid re-running the same
    // ~20-query fan-out (getAllEstimated1RMs + one workout_logs query per
    // top-20 exercise) a second time.
    if (preloadedSummary) {
      setSummary(preloadedSummary);
      setLoading(false);
      return;
    }
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    getStrengthProgressSummary(userId).then(s => {
      if (cancelled) return;
      setSummary(s);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId, preloadedSummary]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ ...typography.sectionHeading, color: colors.text }}>Strength Progress</div>
        <div style={{ textAlign: 'center', padding: spacing['2xl'], color: colors.textMuted }}>Loading...</div>
      </div>
    );
  }

  if (!summary || (summary.topImprovements.length === 0 && summary.needsAttention.length === 0 && summary.newBaselines.length === 0)) {
    return null;
  }

  const maxRM = Math.max(
    1,
    ...summary.topImprovements.map(e => e.estimated1RM),
    ...summary.needsAttention.map(e => e.estimated1RM),
    ...summary.newBaselines.map(e => e.estimated1RM),
  );

  return (
    <div style={styles.container}>
      <div style={{ ...typography.sectionHeading, color: colors.text, marginBottom: spacing.lg }}>Strength Progress</div>

      <StrengthTierList title="Top Improvements" items={summary.topImprovements} variant="improvement" maxRM={maxRM} showRange={showRange} />
      <StrengthTierList title="Needs Attention" items={summary.needsAttention} variant="attention" maxRM={maxRM} showRange={showRange} />
      <StrengthTierList title="New Baselines" items={summary.newBaselines} variant="baseline" maxRM={maxRM} showRange={showRange} />

      {summary.allExercises.length > 0 && (
        <button
          onClick={() => onViewAll(summary.allExercises)}
          style={styles.viewAllBtn}
        >
          View all {summary.allExercises.length} exercises →
        </button>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    backgroundColor: colors.surface1,
    borderRadius: radius.card,
    border: `1px solid ${colors.border}`,
    marginBottom: spacing.lg,
  },
  viewAllBtn: {
    width: '100%',
    padding: spacing.md,
    marginTop: spacing.sm,
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    color: colors.gold,
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
  },
};

export default StrengthProgress;
